import React from 'react';
import { Input } from '../ui/input';
import { Button } from '../ui/button';
import { Loader2, MessageCircle } from 'lucide-react';

/**
 * WhatsApp OTP request + verify panel. The parent owns all state to
 * keep the OTP countdown timer and last_auth localStorage hand-off in
 * one place. This component is pure UI.
 */
export const WhatsAppOtpPanel = ({
  open, onCancel,
  otpStage,
  phone, onPhoneChange,
  otp, onOtpChange,
  role, onRoleChange,
  waCountdown,
  waLoading,
  onRequestOtp, onVerifyOtp,
  onChangeNumber,
}) => {
  if (!open) return null;

  return (
    <div
      className="mb-3 rounded-lg border border-emerald-200 bg-emerald-50/50 p-4 space-y-3"
      data-testid="auth-whatsapp-panel"
    >
      {otpStage === 'phone' && (
        <>
          <label className="text-xs text-slate-600 font-semibold">
            Phone (with country code)
          </label>
          <Input
            value={phone}
            onChange={(e) => onPhoneChange(e.target.value.replace(/[^\d+]/g, ''))}
            placeholder="+447950523316"
            data-testid="auth-whatsapp-phone"
            className="bg-white"
          />
          <div className="flex gap-2">
            <Button
              onClick={onRequestOtp}
              disabled={waLoading}
              data-testid="auth-whatsapp-send-code"
              className="flex-1 bg-[#25D366] hover:bg-[#1FAD52] text-white"
            >
              {waLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Send WhatsApp Code'}
            </Button>
            <Button variant="ghost" onClick={onCancel} className="text-slate-500">
              Cancel
            </Button>
          </div>
        </>
      )}

      {otpStage === 'code' && (
        <>
          <label className="text-xs text-slate-600 font-semibold">
            4-digit code from WhatsApp
          </label>
          <Input
            value={otp}
            onChange={(e) => onOtpChange(e.target.value.replace(/\D/g, '').slice(0, 6))}
            placeholder="0000"
            maxLength={6}
            inputMode="numeric"
            data-testid="auth-whatsapp-code"
            className="bg-white text-center text-xl tracking-[0.4em] font-mono"
          />
          <div className="flex items-center justify-between text-xs text-slate-500">
            {waCountdown > 0 ? (
              <span data-testid="auth-otp-countdown">
                Expires in {Math.floor(waCountdown / 60)}:
                {(waCountdown % 60).toString().padStart(2, '0')}
              </span>
            ) : (
              <button
                type="button"
                onClick={onRequestOtp}
                className="text-emerald-700 underline"
              >
                Resend
              </button>
            )}
            <button
              type="button"
              onClick={onChangeNumber}
              className="text-slate-500"
            >
              Change number
            </button>
          </div>
          <label className="block text-xs text-slate-600 font-semibold mt-1">
            I am a
          </label>
          <select
            value={role}
            onChange={(e) => onRoleChange(e.target.value)}
            data-testid="auth-whatsapp-role"
            className="w-full bg-white border border-slate-200 rounded-md px-3 py-2 text-sm"
          >
            <option value="buyer">Buyer</option>
            <option value="tenant">Tenant</option>
            <option value="seller">Seller</option>
            <option value="agent">Estate Agent</option>
            <option value="professional">Professional / Service Partner</option>
            <option value="investor">Investor / NRI</option>
          </select>
          <Button
            onClick={onVerifyOtp}
            disabled={waLoading || otp.length < 4}
            data-testid="auth-whatsapp-verify"
            className="w-full bg-[#25D366] hover:bg-[#1FAD52] text-white"
          >
            {waLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Verify & Continue'}
          </Button>
        </>
      )}
    </div>
  );
};

export default WhatsAppOtpPanel;
